import { createTheme } from "@mui/material/styles"

// ─── Brand Color Palette ──────────────────────────────────────────────────────
export const brandColors = {
  navy: "#0d1b2a",         // primary — deep ocean navy
  gold: "#c9a84c",         // secondary — warm sand/gold
  bgDefault: "#0a0f14",    // darkest background
  bgPaper: "#111820",      // card/surface background
  textPrimary: "#f0ede8",  // warm white
  textSecondary: "#8a9bb0", // muted blue-grey
  borderSubtle: "#1e2d3d", // subtle border tone
}

// Kept for backwards-compat with any files that import darkBlue
export const darkBlue = brandColors.navy

export const userTheme = createTheme({
  // ─── Palette ───────────────────────────────────────────────────────────────
  palette: {
    mode: "dark",
    primary: {
      main: brandColors.navy,
      light: "#1a2f45",
      dark: "#060d14",
      contrastText: brandColors.textPrimary,
    },
    secondary: {
      main: brandColors.gold,
      light: "#d9bc78",
      dark: "#a07830",
      contrastText: brandColors.bgDefault,
    },
    background: {
      default: brandColors.bgDefault,
      paper: brandColors.bgPaper,
    },
    text: {
      primary: brandColors.textPrimary,
      secondary: brandColors.textSecondary,
    },
    divider: brandColors.borderSubtle,
  },

  // ─── Typography ────────────────────────────────────────────────────────────
  typography: {
    fontFamily: '"Josefin Sans", sans-serif',
    // Display / logo headings → Kenilla (loaded via @font-face in index.css)
    h1: {
      fontFamily: '"Kenilla", "Playfair Display", serif',
      fontWeight: 400,
      letterSpacing: "0.02em",
      color: brandColors.textPrimary,
    },
    h2: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 400,
      letterSpacing: "0.01em",
      color: brandColors.textPrimary,
    },
    h3: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 400,
      color: brandColors.textPrimary,
    },
    h4: {
      fontFamily: '"Josefin Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: brandColors.textPrimary,
    },
    h5: {
      fontFamily: '"Josefin Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: brandColors.textPrimary,
    },
    h6: {
      fontFamily: '"Josefin Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: "0.05em",
      color: brandColors.textPrimary,
    },
    body1: {
      fontFamily: '"Josefin Sans", sans-serif',
      fontWeight: 300,
      lineHeight: 1.75,
      color: brandColors.textPrimary,
    },
    body2: {
      fontFamily: '"Josefin Sans", sans-serif',
      fontWeight: 300,
      lineHeight: 1.65,
      color: brandColors.textSecondary,
    },
    caption: {
      fontFamily: '"Josefin Sans", sans-serif',
      fontWeight: 300,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: brandColors.textSecondary,
    },
    overline: {
      fontFamily: '"Josefin Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: "0.14em",
      color: brandColors.gold,
    },
  },

  // ─── Shape ─────────────────────────────────────────────────────────────────
  shape: {
    borderRadius: 4,
  },

  // ─── Component Overrides ───────────────────────────────────────────────────
  components: {
    // --- Typography ---
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Josefin Sans", sans-serif',
        },
      },
    },

    // --- Button: pill shape with hover transitions ---
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 50,
          fontFamily: '"Josefin Sans", sans-serif',
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          fontSize: "0.75rem",
          padding: "10px 28px",
          transition: "all 0.3s ease",
        },
        containedPrimary: {
          backgroundColor: brandColors.gold,
          color: brandColors.bgDefault,
          "&:hover": {
            backgroundColor: brandColors.gold,
            filter: "brightness(1.1)",
            transform: "translateY(-1px)",
            boxShadow: `0 6px 20px rgba(201, 168, 76, 0.3)`,
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        containedSecondary: {
          backgroundColor: brandColors.navy,
          color: brandColors.textPrimary,
          border: `1px solid ${brandColors.borderSubtle}`,
          "&:hover": {
            backgroundColor: brandColors.bgPaper,
            borderColor: brandColors.gold,
            transform: "translateY(-1px)",
          },
        },
        outlined: {
          borderColor: brandColors.gold,
          color: brandColors.gold,
          "&:hover": {
            backgroundColor: "rgba(201, 168, 76, 0.08)",
            borderColor: brandColors.gold,
            transform: "translateY(-1px)",
          },
        },
        outlinedSecondary: {
          borderColor: brandColors.borderSubtle,
          color: brandColors.textSecondary,
          "&:hover": {
            borderColor: brandColors.textSecondary,
            backgroundColor: "rgba(138, 155, 176, 0.08)",
          },
        },
        text: {
          color: brandColors.gold,
          "&:hover": {
            backgroundColor: "rgba(201, 168, 76, 0.08)",
          },
        },
      },
    },

    // --- AppBar ---
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          backdropFilter: "blur(12px)",
          boxShadow: "none",
          borderBottom: `1px solid ${brandColors.borderSubtle}`,
        },
      },
    },

    // --- Card / Paper ---
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.bgPaper,
          backgroundImage: "none",
          border: `1px solid ${brandColors.borderSubtle}`,
          borderRadius: 8,
          transition: "border-color 0.3s ease, transform 0.3s ease",
          "&:hover": {
            borderColor: `rgba(201, 168, 76, 0.35)`,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.bgPaper,
          backgroundImage: "none",
        },
      },
    },

    // --- Divider ---
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: brandColors.borderSubtle,
        },
      },
    },

    // --- Link ---
    MuiLink: {
      styleOverrides: {
        root: {
          color: brandColors.gold,
          textDecoration: "none",
          transition: "opacity 0.2s ease",
          "&:hover": {
            opacity: 0.75,
          },
        },
      },
    },

    // --- Input / TextField ---
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: '"Josefin Sans", sans-serif',
          color: brandColors.textPrimary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: brandColors.borderSubtle,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: brandColors.textSecondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: brandColors.gold,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Josefin Sans", sans-serif',
          color: brandColors.textSecondary,
          "&.Mui-focused": {
            color: brandColors.gold,
          },
        },
      },
    },

    // --- IconButton ---
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: brandColors.textSecondary,
          transition: "color 0.2s ease",
          "&:hover": {
            color: brandColors.gold,
            backgroundColor: "rgba(201, 168, 76, 0.08)",
          },
        },
      },
    },

    // --- Chip ---
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Josefin Sans", sans-serif',
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontSize: "0.65rem",
          borderRadius: 50,
        },
        outlinedPrimary: {
          borderColor: brandColors.gold,
          color: brandColors.gold,
        },
      },
    },

    // --- CssBaseline: pull global resets into MUI's baseline ─────────────
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brandColors.bgDefault,
          color: brandColors.textPrimary,
          fontFamily: '"Josefin Sans", sans-serif',
        },
      },
    },
  },
})
